import { useEffect, useState } from "react";
import { Bell, BellRing, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../api";
import { useAuth } from "../context/AuthContext";

export default function FollowKitchenButton({ vendorId, vendorName = "this business", compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: user?.role === "customer", following: false, emailAlerts: false, emailAvailable: false, completedOrders: 0, relationship: "new" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!vendorId || user?.role !== "customer") {
      setState({ loading: false, following: false, emailAlerts: false, emailAvailable: false, completedOrders: 0, relationship: "new" });
      return () => { cancelled = true; };
    }
    setState((current) => ({ ...current, loading: true }));
    api.get(`/retention/state/vendor/${vendorId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setState({
            loading: false,
            following: Boolean(data?.following),
            emailAlerts: Boolean(data?.email_alerts),
            emailAvailable: Boolean(data?.email_channel_available),
            completedOrders: Number(data?.completed_orders_here) || 0,
            relationship: data?.relationship || "new",
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, following: false, emailAlerts: false, emailAvailable: false, completedOrders: 0, relationship: "new" });
      });
    return () => { cancelled = true; };
  }, [user?.role, vendorId]);

  if (user && user.role !== "customer") return null;

  const requireCustomer = () => {
    if (user?.role === "customer") return true;
    toast.info("Create a free customer account to follow local food businesses.");
    navigate("/login");
    return false;
  };

  const toggleFollow = async () => {
    if (!requireCustomer() || busy) return;
    setBusy(true);
    try {
      if (state.following) {
        await api.delete(`/retention/follows/${vendorId}`);
        setState((current) => ({ ...current, loading: false, following: false, emailAlerts: false }));
        toast.success(`You stopped following ${vendorName}.`);
      } else {
        await api.post(`/retention/follows/${vendorId}`, { email_alerts: false });
        setState((current) => ({ ...current, loading: false, following: true, emailAlerts: false }));
        toast.success(`Following ${vendorName}. New updates will appear in Saved & alerts.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || "Unable to update this follow.");
    } finally {
      setBusy(false);
    }
  };

  const toggleEmail = async () => {
    if (!state.following || busy) return;
    setBusy(true);
    const next = !state.emailAlerts;
    try {
      await api.patch(`/retention/follows/${vendorId}`, { email_alerts: next });
      setState((current) => ({ ...current, emailAlerts: next }));
      toast.success(next ? "Email business alerts are on." : "Email business alerts are off. In-app alerts stay on.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "marketing_opt_in_required") {
        toast.info("Turn on Arctic Plate updates in Account before enabling email business alerts.");
      } else if (detail?.code === "retention_email_unavailable") {
        setState((current) => ({ ...current, emailAlerts: false, emailAvailable: false }));
        toast.info("Email business alerts are not available yet. In-app alerts are still on.");
      } else {
        toast.error(detail?.message || detail || "Unable to update email alerts.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="retention-follow-controls">
      <button
        type="button"
        onClick={toggleFollow}
        disabled={busy || state.loading}
        data-testid="retention-follow-button"
        aria-pressed={state.following}
        className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition disabled:opacity-50 ${
          state.following
            ? "border-[#f0c79f] bg-[#fff4ec] text-[#a94100]"
            : "border-[#d8d6d2] bg-white text-[#1f2421] hover:border-[#d35400]"
        }`}
      >
        <Heart size={15} className={state.following ? "fill-current" : ""} aria-hidden="true" />
        {state.loading ? "Checking…" : state.following ? "Following" : (compact ? "Follow" : "Follow business")}
      </button>

      {!compact && state.following && state.emailAvailable && (
        <button
          type="button"
          onClick={toggleEmail}
          disabled={busy}
          data-testid="retention-email-alert-toggle"
          aria-pressed={state.emailAlerts}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#d8d6d2] bg-white px-4 py-2 text-xs font-semibold text-[#4b5563] hover:border-[#d35400] disabled:opacity-50"
          title="In-app alerts are always included when you follow. Email is optional."
        >
          {state.emailAlerts ? <BellRing size={15} aria-hidden="true" /> : <Bell size={15} aria-hidden="true" />}
          {state.emailAlerts ? "Email alerts on" : "Add email alerts"}
        </button>
      )}
      {!compact && state.following && !state.emailAvailable && (
        <span className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#e5e7eb] bg-[#f8fafc] px-4 py-2 text-xs font-semibold text-[#6b7280]" title="In-app alerts are active. Email alerts are not currently available.">
          <Bell size={15} aria-hidden="true" />
          In-app alerts on
        </span>
      )}
      {!compact && state.relationship === "regular" && (
        <span className="inline-flex min-h-11 items-center rounded-full bg-[#1f2421] px-4 py-2 text-xs font-semibold text-white" data-testid="retention-regular-badge">
          Regular · {state.completedOrders} completed orders
        </span>
      )}
      {!compact && state.relationship === "supporter" && (
        <span className="inline-flex min-h-11 items-center rounded-full border border-[#c9dfd0] bg-[#eef8f1] px-4 py-2 text-xs font-semibold text-[#0f6b3a]">
          You’ve supported this business before
        </span>
      )}
    </div>
  );
}
